<template>
  <div :class="$style.previewContainer">
    <template
      v-if="isBundleCategory(currentCategoryKey)"
    >
      <ItemPreview
        v-if="isStandaloneItemCategory(currentCategoryKey)"
        :item="standaloneItem"
        :item-to-image-mapper="standaloneItemImageMapper"
      />
      <BundlePreview
        v-else
        :items="selectedBundleItems"
        :item-to-image-mapper="bundleItemImageMapper"
      />
    </template>
    <BundlePreview
      v-else-if="isBowlPartCategory(currentCategoryKey)"
      :items="mainItems"
      :item-to-image-mapper="bundleItemImageMapper" />
    <BurgerPreview
      v-else-if="isBurgerPartCategory(currentCategoryKey)"
      :items="mainItems"
    />
    <ItemPreview
      v-else
      :item="standaloneItem"
      :item-to-image-mapper="standaloneItemImageMapper"
    />
  </div>
</template>

<script>

import { ItemImageTypes } from '@/apps/menu/constants';
import helpers from '@/lib/helpers';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Getter } from 'vuex-class';
import BundlePreview from './BundlePreview';
import BurgerPreview from './BurgerPreview';
import ItemPreview from './ItemPreview';

/**
 * @typedef SelectorByKey
 * @type {(key: string) => T}
 * @template T
 */

/**
 * Order Preview that displays user's order. Depending on a `currentCategoryKey`, it's going to show `drink`, `bundle` or `burger`.
 *
 * @property {number} currentCategoryKey Current key of a category that's being configured.
 */
@Component({
  name:  'OrderPreview',
  props: {
    currentCategoryKey: {
      type:     Number,
      required: true,
    },
  },
  components: {
    BurgerPreview,
    ItemPreview,
    BundlePreview,
  },
})
export default class OrderPreview extends Vue {
  /** @type {SelectorByKey<boolean>} */
  @Getter isStandaloneItemCategory;

  /** @type {SelectorByKey<boolean>} */
  @Getter isBundleCategory;

  /** @type {boolean} */
  @Getter isBurgerPartCategory;

  /** @type {boolean} */
  @Getter isBowlPartCategory;

  /** @type {readonly CategoryItem[]} */
  @Getter selectedItems;

  /** @type {SelectorByKey<boolean>} */
  @Getter isBundleItem;

  /** @type {SelectorByKey<boolean>} */
  @Getter isStandaloneItem;

  get selectedBundleItems() {
    return this.selectedItems.filter(item =>
      this.isBundleItem(item.key) && !this.isStandaloneItem(item.key));
  }

  get standaloneItem() {
    const selectedStandaloneItems = this.selectedItems
      .filter(item => this.isStandaloneItem(item.key));

    if (this.isStandaloneItemCategory(this.currentCategoryKey)) {
      return selectedStandaloneItems.find(item =>
        item.categoryKey === this.currentCategoryKey);
    }

    return selectedStandaloneItems[0];
  }

  get mainItems() {
    return this.selectedItems.filter(item =>
      !this.isBundleItem(item.key) && !this.isStandaloneItem(item.key));
  }

  bundleItemImageMapper = (item) => {
    const bundleImageTypes = [
      ItemImageTypes.configuratorFriesPreviewImage,
      ItemImageTypes.configuratorBowlPreviewImage,
    ];

    const images =
      item.images.filter(image => bundleImageTypes.includes(image.type));

    return helpers.getImagePath(images[0].url);
  };

  standaloneItemImageMapper =
    (item) => {
      const highQualityImageTypes = [
        ItemImageTypes.configuratorFriesPreviewImage,
        ItemImageTypes.configuratorBowlPreviewImage,
        ItemImageTypes.configuratorBurgerPreviewImage,
      ];
      const image =
        item.images.find(image => highQualityImageTypes.includes(image.type));

      return image ? helpers.getImagePath(image.url) : undefined;
    };
}
</script>

<style module>
  .previewContainer {
    width: 100%;
    height: 100%;
  }

  .previewContainer::after {
    content: '';
    padding-top: 70%;
    width: 100%;
  }
</style>



// WEBPACK FOOTER //
// src/apps/configurator/components/Preview/OrderPreview.vue